// proxy.ts — browser-only review-proxy transcoder (EGO-136 workstream 1).
//
// Turns a session's left.mp4 (1280x1280@60fps H.264 High L5.2, moov at the
// END of the file) into derived/proxy.mp4: 360x360, 30fps, ~250 kbps H.264,
// faststart. Runs on WebCodecs via mediabunny, so unlike the rest of core/
// this module does NOT run in node — it must never be imported from the node
// entrypoints (nodeSource.ts, the parity tests) or `cd core && npm test`
// breaks. Portal and the desktop renderer import it directly by path into
// their esbuild bundles; mediabunny (core's only dependency, browser-only in
// practice) resolves from core/node_modules by the usual upward walk.
//
// Spec decisions (benchmarked — don't "improve" them casually):
// - bitrate is an EXPLICIT number, never a mediabunny Quality preset:
//   QUALITY_LOW starves 360p down to ~0.13 Mbps mush. 250 kbps is the
//   measured sweet spot. Callers should log stats.actualBps — hardware
//   encoders sometimes deliver ~2x the requested bitrate at higher
//   resolutions (360p obeys; we still want the telemetry).
// - fastStart "in-memory": originals carry the moov atom at the end (the
//   recorder finalizes it last), which forces a full download before
//   playback can start. Proxies exist to be scrubbed over the network, so
//   their moov must come FIRST. Reading moov-at-end originals over HTTP
//   needs no atom parsing here either — UrlSource handles it natively via
//   ranged reads (measured: 3 requests, 1.005x byte amplification).
// - fit "fill": source and proxy are both square, so no aspect logic can
//   ever letterbox; "fill" keeps that true if dimensions are overridden.
// - audio is discarded: audio.wav is the audio of record, and the proxy is
//   for visual scrubbing only.
//
// Time mapping: 30fps output means NO frame-index parity with
// left.timestamps. Consumers map annotation time → nearest left.timestamps
// entry → t_ns; never proxy frame index → source frame index.

import {
  BlobSource,
  BufferSource,
  BufferTarget,
  Conversion,
  Input,
  MP4,
  Mp4OutputFormat,
  Output,
  QTFF,
  UrlSource,
} from "mediabunny";

export interface ProxyStats {
  /** Wall-clock transcode time, in seconds. */
  seconds: number;
  inputBytes: number;
  outputBytes: number;
  /** outputBytes*8/durationS — log it (encoder overshoot telemetry, above). */
  actualBps: number;
  /** Media duration in seconds (30fps resampling preserves duration). */
  durationS: number;
  /** Transcode cost: wall-clock ms per minute of media. */
  msPerMinute: number;
}

export interface ProxyOpts {
  width?: number;
  height?: number;
  frameRate?: number;
  bitrate?: number;
  keyFrameInterval?: number;
  onProgress?: (frac: number) => void;
}

export type ProxySource = Blob | ArrayBuffer | { url: string; fetchFn?: typeof fetch };

/** Transcode one original (left.mp4) into review-proxy bytes + telemetry. */
export async function makeProxy(
  source: ProxySource,
  opts: ProxyOpts = {},
): Promise<{ bytes: Uint8Array; stats: ProxyStats }> {
  const t0 = performance.now();
  const src =
    source instanceof Blob
      ? new BlobSource(source)
      : source instanceof ArrayBuffer
        ? new BufferSource(source)
        : new UrlSource(source.url, source.fetchFn ? { fetchFn: source.fetchFn } : undefined);
  // Originals are always our own ISOBMFF; restricting formats makes a bad
  // input (e.g. an HTML error page fetched instead of the mp4) fail fast,
  // and lets the unused demuxers tree-shake out of consumer bundles.
  const input = new Input({ formats: [MP4, QTFF], source: src });
  try {
    const output = new Output({
      format: new Mp4OutputFormat({ fastStart: "in-memory" }),
      target: new BufferTarget(),
    });
    const conversion = await Conversion.init({
      input,
      output,
      video: {
        width: opts.width ?? 360,
        height: opts.height ?? 360,
        fit: "fill",
        frameRate: opts.frameRate ?? 30,
        bitrate: opts.bitrate ?? 250_000,
        keyFrameInterval: opts.keyFrameInterval ?? 2,
        forceTranscode: true,
      },
      audio: { discard: true },
      showWarnings: false, // discardedTracks are surfaced in the error below
    });
    if (!conversion.isValid) {
      const reasons = conversion.discardedTracks
        .map((d) => `${d.track.type} track ${d.track.id}: ${d.reason}`)
        .join("; ");
      throw new Error(
        `proxy conversion is invalid — ${reasons || "no usable tracks in input"}`,
      );
    }
    const { onProgress } = opts;
    if (onProgress) conversion.onProgress = (frac) => onProgress(frac); // must be set before execute()
    // Both are memoized and already resolved from the parsed moov (for
    // UrlSource the size fell out of the ranged reads init() performed).
    const [inputBytes, durationS] = await Promise.all([
      src.getSize(),
      input.computeDuration(),
    ]);
    await conversion.execute();
    const buffer = output.target.buffer;
    if (!buffer) throw new Error("proxy transcode finished without an output buffer");
    const seconds = (performance.now() - t0) / 1000;
    const outputBytes = buffer.byteLength;
    return {
      bytes: new Uint8Array(buffer),
      stats: {
        seconds,
        inputBytes,
        outputBytes,
        actualBps: (outputBytes * 8) / durationS,
        durationS,
        msPerMinute: (seconds * 1000) / (durationS / 60),
      },
    };
  } finally {
    input.dispose();
  }
}
